import { useEffect, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/config/routes';
import { StatusDot } from '@/components/ui/StatusDot';
import { EditorialImage } from '@/components/editorial/EditorialImage';
import type { MockProfile } from '@/data/mockProfiles';
import { capture } from '@/modules/heat/signals';

export type CardFace = 'stats' | 'photo';

interface FeedCardProps {
  profile: MockProfile;
  index: number;
  /** Editorial plate number shown in the corner — defaults to index+1. */
  plate?: number;
  /** Tighter card variant for desktop two-column rhythm. */
  dense?: boolean;
  /**
   * Which face renders by default. The other becomes the flip target.
   * Auto-rotates back to this face after 5 seconds when revealed.
   */
  defaultFace?: CardFace;
}

const BAND_LABEL: Record<MockProfile['band'], string> = {
  pulse: 'pulse',
  hot: 'heat',
  warm: 'signal',
  cool: 'idle',
};

/**
 * Stats-first flip card.
 *
 *   Front: editorial dark face — handle + mood + dating stats grid +
 *          looking-for pills + HEAT meter.
 *   Back:  the photo (matte colour, scrim) with a one-line summary +
 *          a "send a signal" CTA.
 *
 * Tap anywhere on the card body to flip. Card auto-rotates back to the
 * stats face after 5 seconds. The handle and the action chevron always
 * navigate to the profile detail (they stop propagation so they don't flip).
 *
 * 3D flip uses the .hottr-flip-* utilities in hottr.base.css. Mechanical
 * ease, 400ms — no spring overshoot per brand canon.
 */
export const FeedCard = ({
  profile,
  index,
  plate,
  dense = false,
  defaultFace = 'stats',
}: FeedCardProps) => {
  const navigate = useNavigate();
  const [flipped, setFlipped] = useState(false);
  const plateNumber = plate ?? index + 1;

  // If the user changes the default face (e.g. via the FeedPage toggle),
  // snap any flipped cards back to the new default.
  useEffect(() => {
    setFlipped(false);
  }, [defaultFace]);

  // Auto-flip back to the default face after 5s when revealed
  useEffect(() => {
    if (!flipped) return;
    const t = window.setTimeout(() => setFlipped(false), 5000);
    return () => window.clearTimeout(t);
  }, [flipped]);

  const handleFlip = () => setFlipped((v) => !v);

  const handleOpenProfile = (e: MouseEvent) => {
    e.stopPropagation();
    capture('profile_view', 'feed', profile.id, { index });
    navigate(ROUTES.profile(profile.handle));
  };

  // Front = the user's default face. Back = the other.
  // The flip rotates between front and back regardless of which is which.
  const Front = defaultFace === 'stats' ? StatsFace : PhotoFace;
  const Back = defaultFace === 'stats' ? PhotoFace : StatsFace;
  const flipHint = defaultFace === 'stats' ? 'photo' : 'stats';

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1], delay: Math.min(index, 6) * 0.04 }}
      className={cn('relative isolate', dense ? 'aspect-[3/4]' : 'aspect-[4/5]')}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={`@${profile.handle} — tap to see ${flipped ? defaultFace : flipHint}`}
        aria-pressed={flipped}
        onClick={handleFlip}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleFlip();
          }
        }}
        className={cn(
          'hottr-flip-perspective absolute inset-0 cursor-pointer',
          'border border-white/[0.06] transition-[border-color] duration-base ease-signal',
          'hover:border-pink/40 focus-visible:border-pink',
          'focus:outline-none',
        )}
      >
        <div className={cn('hottr-flip-inner', flipped && 'is-flipped')}>
          {/* FRONT — defaultFace */}
          <div className="hottr-flip-face">
            <Front
              profile={profile}
              plate={plateNumber}
              onOpen={handleOpenProfile}
              flipHint={flipHint}
            />
          </div>

          {/* BACK — the other face */}
          <div className="hottr-flip-face back">
            <Back
              profile={profile}
              plate={plateNumber}
              onOpen={handleOpenProfile}
              flipHint={defaultFace}
            />
          </div>
        </div>
      </div>
    </motion.article>
  );
};

/* ============================================================
   FRONT — STATS FACE
   Dark editorial card. Handle, mood, stats grid, looking-for, HEAT.
   ============================================================ */

interface FaceProps {
  profile: MockProfile;
  plate: number;
  onOpen: (e: MouseEvent) => void;
  /** What this face flips to — drives the bottom hint copy. */
  flipHint: CardFace;
}

const StatsFace = ({ profile, plate, onOpen }: FaceProps) => (
  <div className="relative h-full w-full overflow-hidden bg-charcoal/60 text-text-primary">
    {/* Soft pink-tinted top fade — keeps the face from feeling flat black */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-2/3"
      style={{
        background:
          'radial-gradient(80% 90% at 50% 0%, rgba(255,12,182,0.04) 0%, transparent 65%), linear-gradient(180deg, rgba(20,21,26,0.85) 0%, rgba(5,6,8,0.9) 100%)',
      }}
    />

    <div className="relative flex h-full flex-col gap-4 px-5 py-5 md:px-6 md:py-6">
      {/* TOP — plate + presence */}
      <header className="flex items-start justify-between">
        <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-secondary">
          <span className="text-pink">No.</span>{' '}
          {String(plate).padStart(2, '0')}
        </span>
        {profile.presence === 'live' && (
          <span className="inline-flex items-center gap-1.5 font-display text-[9px] font-bold uppercase tracking-widest text-pink">
            <StatusDot status="live" size="h-1.5 w-1.5" />
            live
          </span>
        )}
      </header>

      {/* HANDLE + meta */}
      <div>
        <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          {profile.location} · {profile.distance}
          {profile.age && ` · ${profile.age}`}
        </p>
        <button
          type="button"
          onClick={onOpen}
          className="mt-1 block text-left font-display font-black uppercase leading-[0.92] tracking-display text-text-primary transition-colors duration-fast ease-signal hover:text-pink"
          style={{ fontSize: 'clamp(28px, 4vw, 38px)' }}
        >
          {profile.handle}<span className="text-pink">.</span>
        </button>
        <p className="mt-2 line-clamp-2 text-sm text-text-secondary">
          {profile.mood}
        </p>
      </div>

      {/* STATS GRID — the core of the front */}
      <dl className="divide-y divide-white/[0.06] border-y border-white/[0.07]">
        <Stat label="Height" value={profile.height} />
        <Stat label="Weight" value={profile.weight} />
        <Stat label="Body" value={profile.body} />
        <Stat label="Position" value={profile.position} pink />
        {profile.tribe && <Stat label="Tribe" value={profile.tribe} />}
        <Stat label="Status" value={profile.status} pink />
      </dl>

      {/* LOOKING FOR */}
      <div className="flex flex-wrap gap-1.5">
        {profile.lookingFor.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="border border-pink/30 bg-pink/[0.05] px-2 py-0.5 font-display text-[9px] font-bold uppercase tracking-widest text-pink"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* FOOTER — HEAT meter only */}
      <div className="mt-auto flex items-center gap-3">
        <span className="font-display text-[9px] font-bold uppercase tracking-widest text-pink">
          {BAND_LABEL[profile.band]}
        </span>
        <span aria-hidden className="h-px flex-1 bg-white/15" />
        <BandMeter band={profile.band} />
      </div>
    </div>
  </div>
);

const Stat = ({
  label,
  value,
  pink = false,
}: {
  label: string;
  value: string;
  pink?: boolean;
}) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <dt className="font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
      {label}
    </dt>
    <dd
      className={cn(
        'font-display text-[11px] font-bold uppercase tracking-wide',
        pink ? 'text-pink' : 'text-text-primary',
      )}
    >
      {value}
    </dd>
  </div>
);

const BandMeter = ({ band }: { band: MockProfile['band'] }) => {
  const order = { cool: 1, warm: 2, hot: 3, pulse: 4 } as const;
  const filled = order[band];
  return (
    <div className="flex gap-0.5" aria-hidden>
      {Array.from({ length: 4 }).map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-1.5 w-3',
            i < filled ? 'bg-pink shadow-glow-soft' : 'bg-white/15',
            band === 'pulse' && i < filled && 'animate-pulse-neon',
          )}
        />
      ))}
    </div>
  );
};

/* ============================================================
   BACK — PHOTO FACE
   Matte-colour photo, ink scrim, handle/mood overlay, action chevron.
   ============================================================ */

const PhotoFace = ({ profile, onOpen }: FaceProps) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const photos = profile.photos.length > 0 ? profile.photos : [''];
  const hasGallery = photos.length > 1;

  const advance = (e: MouseEvent) => {
    if (!hasGallery) return;
    e.stopPropagation();
    setPhotoIndex((i) => (i + 1) % photos.length);
  };

  return (
    <div className="relative h-full w-full overflow-hidden">
      <EditorialImage
        scene={profile.scene}
        seed={profile.sceneSeed + photoIndex * 3}
        photoUrl={photos[photoIndex]}
        aspect="aspect-auto"
        className="h-full"
      />

      {/* Tap-right invisible zone — cycles photos without triggering flip */}
      {hasGallery && (
        <button
          type="button"
          aria-label="Next photo"
          onClick={advance}
          className="absolute inset-y-0 right-0 z-10 w-1/3 cursor-pointer"
        />
      )}

      {/* TOP ROW — gallery dots (left) + live indicator (right) */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-3 p-5 md:p-6">
        {hasGallery ? (
          <span className="pointer-events-auto inline-flex items-center gap-1">
            {photos.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Photo ${i + 1}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotoIndex(i);
                }}
                className={cn(
                  'h-1 transition-all duration-fast ease-signal',
                  i === photoIndex
                    ? 'w-7 bg-pink shadow-glow-soft'
                    : 'w-5 bg-white/30 hover:bg-white/60',
                )}
              />
            ))}
          </span>
        ) : (
          <span />
        )}
        {profile.presence === 'live' && (
          <span className="pointer-events-none inline-flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
            <StatusDot status="live" size="h-1.5 w-1.5" />
            Live
          </span>
        )}
      </div>

      {/* BOTTOM — meta · handle · mood. The handle carries the card. */}
      <footer className="absolute inset-x-0 bottom-0 z-10 p-5 md:p-7">
        <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
          {profile.location} · {profile.distance}
          {profile.age && ` · ${profile.age}`}
        </p>
        <button
          type="button"
          onClick={onOpen}
          className="mt-1.5 block text-left font-display font-black uppercase leading-[0.88] tracking-display text-text-primary transition-colors duration-fast ease-signal hover:text-pink"
          style={{
            fontSize: 'clamp(34px, 4.5vw, 56px)',
            letterSpacing: '-0.045em',
          }}
        >
          {profile.handle}<span className="text-pink">.</span>
        </button>
        <p className="mt-3 line-clamp-1 text-sm text-text-secondary md:text-[15px]">
          {profile.mood}
        </p>
        {profile.band === 'pulse' && (
          <span
            aria-hidden
            className="mt-3 block h-px w-12 bg-pink shadow-glow-soft animate-pulse-neon"
          />
        )}
      </footer>
    </div>
  );
};
